import {
	bindable,
	bindingMode,
	customElement,
	PLATFORM,
	useView,
} from 'aurelia-framework';

import {
	DynamicFieldMeta,
	DynamicFieldTable,
	FieldModelRow,
} from '~/components/dynamic-field-table/dynamic-field-table';
import { FieldModel, FormModel } from '~/models/form-model';
import FormModelUtils from '~/utils/form-model-utils';

export enum MoveRecordDirection {
	UP = 'UP',
	DOWN = 'DOWN',
}

@customElement('form-model-list-field-table')
@useView(PLATFORM.moduleName('./form-model-list-field-table.html'))
export class FormModelListFieldTable extends DynamicFieldTable {
	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	private rowIdField: string;

	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	private onRemoveRecord?: (rowIndex: number, totalRows: number) => void;

	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	private onMoveRecord?: () => void;

	/**
	 * If a form contains two field tables, the second field table has no reference to the first field table.
	 * This boolean triggers a calculation that allows us to translate the rowIndex of the second field table
	 * into an index across both field tables. The calculation does not work for more than two field tables.
	 */
	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	private calculateGlobalRowIndex?: boolean = false;

	/**
	 * @override
	 */
	protected updateFieldModelMatrix(): void {
		if (!this.formModel) {
			// Reset to empty if no form model
			this.fieldModelMatrix = [];
			this.fieldAdditionalInfo = [];
			return;
		}

		// rowNum starts with 0 but fields start with 1
		const fieldNameMatrix: string[][] = [];
		for (let rowIndex = 1; rowIndex <= this.maxRows; rowIndex++) {
			const fieldNameRow: string[] = [];
			for (const meta of this.fieldMeta) {
				fieldNameRow.push(
					meta.fieldNamePrefix +
						DynamicFieldTable.getSuffix(rowIndex),
				);
			}
			fieldNameMatrix.push(fieldNameRow);
		}

		// Update field model matrix
		// (only set once to avoid triggering multiple compute events)
		const fieldModelMatrix = [];
		const formModelList = this.formModel.formModelList;
		if (formModelList) {
			let rowIndex = 0;
			this.fieldAdditionalInfo = [];
			formModelList.forEach((formModel) => {
				const lengthBeforePush = fieldModelMatrix.length;
				this.pushRecordForFormModel(fieldModelMatrix, formModel);
				if (
					// If it's confirmed that a record has been pushed, run onRenderAdditionalInfo
					fieldModelMatrix.length > lengthBeforePush &&
					this.onRenderAdditionalInfo
				) {
					this.fieldAdditionalInfo.push(
						this.onRenderAdditionalInfo(rowIndex),
					);
				}
				rowIndex++;
			});
		}

		this.fieldModelMatrix = fieldModelMatrix;
	}

	/**
	 * push record into fieldModelMatrix
	 */
	private pushRecordForFormModel(
		fieldModelMatrix: FieldModelRow[],
		formModel: FormModel,
	): void {
		if (fieldModelMatrix.length >= this.maxRows || !this.rowIdField) {
			return;
		}
		const row = parseInt(formModel.fields[this.rowIdField].value, 10) - 1;
		const fieldModelRow: { [fieldName: string]: FieldModel } = {};
		for (const meta of this.fieldMeta) {
			const fieldModel = DynamicFieldTable.getFieldModel(
				formModel,
				meta.fieldNamePrefix,
				row,
			);
			if (!fieldModel) {
				// do not add row to matrix if fieldModel is undefined
				return;
			}
			fieldModel.forceSave = true;
			fieldModelRow[meta.fieldNamePrefix] = fieldModel;
		}
		fieldModelMatrix.push(fieldModelRow);
	}

	/**
	 * @override
	 */
	protected onClickRemoveRecord(e, rowIndex: number): void {
		// remove record logic is kept in the bindable function because removing records from the form model
		// list is dependent on the global context when there is more than one form model list field table
		if (this.onRemoveRecord) {
			this.onRemoveRecord(rowIndex, this.fieldModelMatrix.length);
		}
	}

	private onClickMoveRecord(
		e,
		rowIndex: number,
		direction: MoveRecordDirection,
	): void {
		if (this.fieldModelMatrix.length <= 1) {
			return;
		}
		let globalRowIndex = rowIndex;
		if (this.calculateGlobalRowIndex) {
			globalRowIndex =
				this.formModel.formModelList.length -
				this.fieldModelMatrix.length +
				rowIndex;
		}
		this.formModel = this.moveRecordAndGetFormModel(
			direction,
			globalRowIndex,
		);
		if (this.onMoveRecord) {
			this.onMoveRecord();
		}
	}

	private moveRecordAndGetFormModel(
		direction: MoveRecordDirection,
		globalRowIndex: number,
	): FormModel {
		const formModelList = this.formModel.formModelList;

		let rowIndexOfSwappedRecord;
		let rowIdOfSwappedRecord;
		let directionOfSwappedRecord;
		if (direction === MoveRecordDirection.DOWN) {
			rowIndexOfSwappedRecord = globalRowIndex + 1;
			rowIdOfSwappedRecord = globalRowIndex + 2;
			directionOfSwappedRecord = MoveRecordDirection.UP;
		} else {
			rowIndexOfSwappedRecord = globalRowIndex - 1;
			rowIdOfSwappedRecord = globalRowIndex;
			directionOfSwappedRecord = MoveRecordDirection.DOWN;
		}

		// update id of source record being moved
		FormModelListFieldTable.updateRowId(
			formModelList[globalRowIndex].fields,
			this.rowIdField,
			this.fieldMeta,
			globalRowIndex + 1,
			direction,
		);
		// update id of record that is being swapped with source record
		FormModelListFieldTable.updateRowId(
			formModelList[rowIndexOfSwappedRecord].fields,
			this.rowIdField,
			this.fieldMeta,
			rowIdOfSwappedRecord,
			directionOfSwappedRecord,
		);

		const formModelToMove = formModelList[globalRowIndex];
		// swap indexes of records whose order is being changed
		formModelList.splice(globalRowIndex, 1);
		formModelList.splice(rowIndexOfSwappedRecord, 0, formModelToMove);

		const updatedFormModel = FormModelUtils.cloneModel(this.formModel);
		updatedFormModel.formModelList = formModelList;
		return updatedFormModel;
	}

	/**
	 * This changes the row ID (as opposed to the zero-based index) of all applicable fields in the provided form
	 * model. This includes the Row ID field as well as the fields in the dynamic field table which are appended with
	 * the row ID suffix.
	 * @param formModelFields	The fields of the formModel to be updated
	 * @param rowIdField		The field of the formModel that determines the priority/row ID of the record
	 * @param dynamicFieldMeta	The ZY fields in the dynamic field table
	 * @param rowId				The current row ID of the record being updated
	 * @param direction			The direction in which the record is being moved. Moving a record down increments the
	 * 							row ID in order to decrease the record priority.
	 */
	public static updateRowId(
		formModelFields: { [key in string]: FieldModel },
		rowIdField: string,
		dynamicFieldMeta: DynamicFieldMeta[],
		rowId: number,
		direction: MoveRecordDirection,
	) {
		let priority = parseInt(formModelFields[rowIdField].value, 10);
		let newRowId = rowId;
		if (direction === MoveRecordDirection.DOWN) {
			priority++;
			newRowId++;
		} else {
			priority--;
			newRowId--;
		}
		formModelFields[rowIdField].value = String(priority);
		for (const meta of dynamicFieldMeta) {
			const currentFieldName =
				meta.fieldNamePrefix + DynamicFieldTable.getSuffix(rowId);
			const newFieldName =
				meta.fieldNamePrefix + DynamicFieldTable.getSuffix(newRowId);
			formModelFields[newFieldName] = {
				...formModelFields[currentFieldName],
				...{ name: newFieldName },
			};
			delete formModelFields[currentFieldName];
		}
	}
}
